// Word lists for the games (Word search, Flashcard sprint, and the games still to come).
// The words themselves live in gameWordsA.js and gameWordsB.js, written once in all five languages,
// so any pair of target language and interface language works without extra content.

import { GAME_WORDS_A } from './gameWordsA';
import { GAME_WORDS_B } from './gameWordsB';
import { loadVocabularyPool, shuffle } from './vocabPool';

const ALL_WORDS = { ...GAME_WORDS_A, ...GAME_WORDS_B };
const CODES = { english: 0, italian: 1, spanish: 2, french: 3, portuguese: 4 };
const INTERFACE_INDEX = { en: 0, it: 1, es: 2, fr: 3, pt: 4 };

// A learner at a level gets that level's words plus the level just below, so the pool is never too small
const LEVELS_FOR = {
    A1: ['A1'],
    A2: ['A2', 'A1'],
    B1: ['B1', 'A2'],
    B2: ['B2', 'B1'],
    C1: ['B2', 'B1'],
    C2: ['B2', 'B1'],
};

// Returns [{ word, translation }] for the language being learned, translated into the interface language.
// When the learner reads the app in the language they study, English is used as the translation
// (Italian when the language studied is English), so a flashcard never shows the word as its own translation.
export function getGameWords(targetLang, interfaceLang, level) {
    const targetIndex = CODES[String(targetLang || 'italian').toLowerCase()];
    const safeTarget = targetIndex === undefined ? CODES.italian : targetIndex;
    let translationIndex = INTERFACE_INDEX[interfaceLang];
    if (translationIndex === undefined) translationIndex = INTERFACE_INDEX.en;
    if (translationIndex === safeTarget) translationIndex = safeTarget === INTERFACE_INDEX.en ? INTERFACE_INDEX.it : INTERFACE_INDEX.en;

    const levels = LEVELS_FOR[level] || LEVELS_FOR.A1;
    const words = [];
    levels.forEach(key => {
        (ALL_WORDS[key] || []).forEach(entry => {
            words.push({ word: entry[safeTarget], translation: entry[translationIndex] });
        });
    });
    return words;
}

// The words a learner practices in the games, in two groups:
//   personal: words from lessons they completed and words in their Word Bank (these reinforce what they are learning)
//   builtIn:  the shared list for their level (so the games are never empty)
export async function loadGamePool(userId, targetLang, interfaceLang, level) {
    let personal = [];
    if (userId) {
        try {
            const all = await loadVocabularyPool(userId, targetLang, interfaceLang);
            // The starter set that vocabPool adds for new learners is left out, the built-in list covers that
            personal = all
                .filter(item => item.source === 'lesson' || item.source === 'wordbank')
                .map(item => ({ word: item.word, translation: item.translation || '', source: item.source }));
        } catch (e) {
            personal = [];
        }
    }
    const seen = new Set(personal.map(item => item.word.toLowerCase()));
    const builtIn = getGameWords(targetLang, interfaceLang, level)
        .filter(item => !seen.has(item.word.toLowerCase()))
        .map(item => ({ ...item, source: 'builtin' }));
    return { personal, builtIn };
}

// Picks `count` words: about 70 percent from the learner's own words when they have enough, the rest from the built-in list.
// `accept` lets each game say which words it can use (the word search needs single words of 3 to 10 letters, for example).
export function pickGameWords(pool, count, accept) {
    const ok = accept || (() => true);
    const personal = shuffle(pool.personal.filter(ok));
    const builtIn = shuffle(pool.builtIn.filter(ok));
    const wantPersonal = Math.min(personal.length, Math.ceil(count * 0.7));
    const chosen = personal.slice(0, wantPersonal);
    chosen.push(...builtIn.slice(0, count - chosen.length));
    // If the built-in list ran short, use more of the learner's own words
    if (chosen.length < count) chosen.push(...personal.slice(wantPersonal, wantPersonal + count - chosen.length));
    return shuffle(chosen);
}

const stripAccents = (w) => String(w || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Translations for every built-in word, keyed by the word without accents.
// Used to show a meaning next to words the games find (for example in the Word Wheel).
export function getTranslationMap(targetLang, interfaceLang) {
    const map = {};
    ['A1', 'A2', 'B1', 'B2'].forEach(level => {
        getGameWords(targetLang, interfaceLang, level).forEach(item => {
            map[stripAccents(item.word)] = item.translation;
        });
    });
    return map;
}